import { DatePipe } from '@angular/common';
import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiCacheService } from '../cache-service/api-cache-service';
import { SKIP_GLOBAL_ERROR } from '../error-handler/error-handler';
import { JwtService } from '../jwt/jwt-service';

@Injectable({
  providedIn: 'root'
})
export class FileService {
  private http = inject(HttpClient);
  private jwtService = inject(JwtService);
  private datePipe = inject(DatePipe);
  private cache = inject(ApiCacheService);

  private baseURL: string = environment.HTTP;
  private baseImport = `Import`;
  private baseImportTemplate = `Import/ImportTemplate`;
  private baseColumns = `Import/CheckAvailableColumns`;

  private apiImportCostType = `${this.baseURL}/${this.baseImport}/CostType`;

  private apiImportTemplateCostType = `${this.baseURL}/${this.baseImportTemplate}/CostType`;

  private getScopeId() {
    const loginInfo = this.jwtService.getClaims();
    if (loginInfo) {
      return loginInfo.scopeid || null;
    }

    return null;
  }

  //#region Credit Terms
  private apiImportTemplateCreditTerm = `${this.baseURL}/${this.baseImportTemplate}/CreditTerm`;
  private apiCreditTermImport = `${this.baseURL}/${this.baseImport}/CreditTerm`;
  private apiGetCreditTermColumns = `${this.baseURL}/${this.baseColumns}/CreditTerm`;

  GetCreditTermColumns() {
    return this.cache.get(`apiGetCreditTermColumns`, () => this.http.get(this.apiGetCreditTermColumns));
  }

  GetImportTemplateCreditTerms(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateCreditTerm}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportCreditTerm(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiCreditTermImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Payment Terms
  private apiImportTemplatePaymentTerm = `${this.baseURL}/${this.baseImportTemplate}/PaymentTerm`;
  private apiPaymentTermImport = `${this.baseURL}/${this.baseImport}/PaymentTerm`;
  private apiGetPaymentTermColumns = `${this.baseURL}/${this.baseColumns}/PaymentTerm`;

  GetPaymentTermColumns() {
    return this.cache.get(`apiGetPaymentTermColumns`, () => this.http.get(this.apiGetPaymentTermColumns));
  }

  GetImportTemplatePaymentTerms(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplatePaymentTerm}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportPaymentTerm(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiPaymentTermImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Supplier
  private apiImportTemplateSupplier = `${this.baseURL}/${this.baseImportTemplate}/Supplier`;
  private apiSupplierImport = `${this.baseURL}/${this.baseImport}/Supplier`;
  private apiGetSupplierColumns = `${this.baseURL}/${this.baseColumns}/Supplier`;

  GetSupplierColumns() {
    return this.cache.get(`apiGetSupplierColumns`, () => this.http.get(this.apiGetSupplierColumns));
  }

  GetImportTemplateSupplier(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateSupplier}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportSupplier(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiSupplierImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Incoterm
  private apiImportTemplateIncoterm = `${this.baseURL}/${this.baseImportTemplate}/Incoterm`;
  private apiIncotermImport = `${this.baseURL}/${this.baseImport}/Incoterm`;
  private apiGetIncotermColumns = `${this.baseURL}/${this.baseColumns}/Incoterm`;

  GetIncotermColumns() {
    return this.cache.get(`apiGetIncotermColumns`, () => this.http.get(this.apiGetIncotermColumns));
  }

  GetImportTemplateIncoterm(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateIncoterm}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportIncoterm(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiIncotermImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Shipping Mode
  private apiImportTemplateShippingMode = `${this.baseURL}/${this.baseImportTemplate}/ShippingMode`;
  private apiShippingModeImport = `${this.baseURL}/${this.baseImport}/ShippingMode`;
  private apiGetShippingModeColumns = `${this.baseURL}/${this.baseColumns}/ShippingMode`;

  GetShippingModeColumns() {
    return this.cache.get(`apiGetShippingModeColumns`, () => this.http.get(this.apiGetShippingModeColumns));
  }

  GetImportTemplateShippingMode(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateShippingMode}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportShippingMode(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiShippingModeImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Port
  private apiImportTemplatePort = `${this.baseURL}/${this.baseImportTemplate}/Port`;
  private apiPortImport = `${this.baseURL}/${this.baseImport}/Port`;
  private apiGetPortColumns = `${this.baseURL}/${this.baseColumns}/Port`;

  GetPortColumns() {
    return this.cache.get(`apiGetPortColumns`, () => this.http.get(this.apiGetPortColumns));
  }

  GetImportTemplatePort(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplatePort}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportPort(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiPortImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Carrier Route
  private apiImportTemplateCarrierRoute = `${this.baseURL}/${this.baseImportTemplate}/CarrierRoute`;
  private apiCarrierRouteImport = `${this.baseURL}/${this.baseImport}/CarrierRoute`;
  private apiGetCarrierRouteColumns = `${this.baseURL}/${this.baseColumns}/CarrierRoute`;

  GetCarrierRouteColumns() {
    return this.cache.get(`apiGetCarrierRouteColumns`, () => this.http.get(this.apiGetCarrierRouteColumns));
  }

  GetImportTemplateCarrierRoute(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateCarrierRoute}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportCarrierRoute(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiCarrierRouteImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Supplier Group
  private apiImportTemplateGroup = `${this.baseURL}/${this.baseImportTemplate}/SupplierGroup`;
  private apiGroupImport = `${this.baseURL}/${this.baseImport}/SupplierGroup`;
  private apiGetGroupColumns = `${this.baseURL}/${this.baseColumns}/SupplierGroup`;

  GetGroupColumns() {
    return this.cache.get(`apiGetGroupColumns`, () => this.http.get(this.apiGetGroupColumns));
  }

  GetImportTemplateGroup(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateGroup}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportGroup(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiGroupImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Entity
  private apiImportEntity = `${this.baseURL}/${this.baseImport}/Entity`;
  private apiImportTemplateEntity = `${this.baseURL}/${this.baseImportTemplate}/Entity`;
  private apiGetEntityColumns = `${this.baseURL}/${this.baseColumns}/Entity`;

  GetEntityColumns() {
    return this.cache.get(`apiGetEntityColumns`, () => this.http.get(this.apiGetEntityColumns));
  }

  GetImportTemplateEntity(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateEntity}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportEntity(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiImportEntity}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Terminal
  private apiImportTemplateTerminal = `${this.baseURL}/${this.baseImportTemplate}/Terminal`;
  private apiImportTerminal = `${this.baseURL}/${this.baseImport}/Terminal`;
  private apiGetTerminalColumns = `${this.baseURL}/${this.baseColumns}/Terminal`;

  GetTerminalColumns() {
    return this.cache.get(`apiGetTerminalColumns`, () => this.http.get(this.apiGetTerminalColumns));
  }

  GetImportTemplateTerminal(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateTerminal}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportTerminal(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiImportTerminal}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Store
  private apiImportStore = `${this.baseURL}/${this.baseImport}/Store`;
  private apiImportTemplateStore = `${this.baseURL}/${this.baseImportTemplate}/Store`;
  private apiGetStoreColumns = `${this.baseURL}/${this.baseColumns}/Store`;

  GetStoreColumns() {
    return this.cache.get(`apiGetStoreColumns`, () => this.http.get(this.apiGetStoreColumns));
  }

  GetImportTemplateStore(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateStore}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportStore(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiImportStore}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Warehouse
  private apiImportWarehouse = `${this.baseURL}/${this.baseImport}/Warehouse`;
  private apiImportTemplateWarehouse = `${this.baseURL}/${this.baseImportTemplate}/Warehouse`;
  private apiGetWarehouseColumns = `${this.baseURL}/${this.baseColumns}/warehouse`;

  GetWarehouseColumns() {
    return this.cache.get(`apiGetWarehouseColumns`, () => this.http.get(this.apiGetWarehouseColumns));
  }

  GetImportTemplateWarehouse(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateWarehouse}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportWarehouse(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiImportWarehouse}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region User
  private apiImportTemplateUser = `${this.baseURL}/${this.baseImportTemplate}/User`;
  private apiUserImport = `${this.baseURL}/${this.baseImport}/User`;
  private apiGetUserColumns = `${this.baseURL}/${this.baseColumns}/User`;

  GetUserColumns() {
    return this.cache.get(`apiGetUserColumns`, () => this.http.get(this.apiGetUserColumns));
  }

  GetImportTemplateUsers(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateUser}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportUser(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiUserImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //region Product
  private apiImportTemplateProduct = `${this.baseURL}/${this.baseImportTemplate}/Product`;
  private apiProductImport = `${this.baseURL}/${this.baseImport}/Product`;
  private apiGetProductColumns = `${this.baseURL}/${this.baseColumns}/Product`;

  GetProductColumns() {
    return this.cache.get(`apiGetProductColumns`, () => this.http.get(this.apiGetProductColumns));
  }

  GetImportTemplateProduct(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateProduct}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportProduct(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiProductImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //region Price Change
  private apiImportTemplatePriceChangeProfile = `${this.baseURL}/${this.baseImportTemplate}/PriceChange`;
  private apiPriceChangeProfileImport = `${this.baseURL}/${this.baseImport}/PriceChange`;
  private apiGetPriceChangeProfileColumns = `${this.baseURL}/${this.baseColumns}/PriceChange`;

  GetPriceChangeProfileColumns() {
    return this.cache.get(`apiGetPriceChangeProfileColumns`, () => this.http.get(this.apiGetPriceChangeProfileColumns));
  }

  GetImportTemplatePriceChangeProfile(data: any) {
    const params = new HttpParams()
      .set('scopeId', data.scopeId)
      .set('priceProfileId', data.priceProfileId)
      .set('plCalculationMethodId', data.plCalculationMethodId);
    return this.http.post(`${this.apiImportTemplatePriceChangeProfile}`, null, {
      params: params,
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportPriceChangeProfile(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiPriceChangeProfileImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //region Cost Change
  private apiImportTemplateCostChangeProfile = `${this.baseURL}/${this.baseImportTemplate}/CostChange`;
  private apiCostChangeProfileImport = `${this.baseURL}/${this.baseImport}/CostChange`;
  private apiGetCostChangeProfileColumns = `${this.baseURL}/${this.baseColumns}/CostChange`;

  GetCostChangeProfileColumns() {
    return this.cache.get(`apiGetCostChangeProfileColumns`, () => this.http.get(this.apiGetCostChangeProfileColumns));
  }

  GetImportTemplateCostChangeProfile(data: any) {
    const params = new HttpParams().set('scopeId', data.scopeId).set('desc', data.desc);
    return this.http.post(`${this.apiImportTemplateCostChangeProfile}`, null, {
      params: params,
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportCostChangeProfile(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiCostChangeProfileImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Prod Group
  private apiImportTemplateProductGroup = `${this.baseURL}/${this.baseImportTemplate}/ProductGroup`;
  private apiProductGroupImport = `${this.baseURL}/${this.baseImport}/ProductGroup`;
  private apiGetProductGroupColumns = `${this.baseURL}/${this.baseColumns}/ProductGroup`;

  GetProductGroupColumns() {
    return this.cache.get(`apiGetProductGroupColumns`, () => this.http.get(this.apiGetProductGroupColumns));
  }

  GetImportTemplateProductGroup(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateProductGroup}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportProductGroup(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiProductGroupImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Prod Attribute
  private apiImportTemplateProductAttribute = `${this.baseURL}/${this.baseImportTemplate}/ProductAttribute`;
  private apiProductAttributeImport = `${this.baseURL}/${this.baseImport}/ProductAttribute`;
  private apiGetProductAttributeColumns = `${this.baseURL}/${this.baseColumns}/ProductAttribute`;

  GetProductAttributeColumns() {
    return this.cache.get(`apiGetProductAttributeColumns`, () => this.http.get(this.apiGetProductAttributeColumns));
  }

  GetImportTemplateProductAttribute(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateProductAttribute}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportProductAttribute(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiProductAttributeImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Prod Tagging
  private apiImportTemplateProductTagging = `${this.baseURL}/${this.baseImportTemplate}/ProductTag`;
  private apiProductTaggingImport = `${this.baseURL}/${this.baseImport}/ProductTag`;
  private apiGetProductTaggingColumns = `${this.baseURL}/${this.baseColumns}/ProductTag`;

  GetProductTaggingColumns() {
    return this.cache.get(`apiGetProductTaggingColumns`, () => this.http.get(this.apiGetProductTaggingColumns));
  }

  GetImportTemplateProductTagging(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateProductTagging}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportProductTagging(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiProductTaggingImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Cost Type
  importCostType(formData: FormData) {
    return this.http.post<any>(this.apiImportCostType, formData, {
      reportProgress: true,
      observe: 'events',
      context: new HttpContext().set(SKIP_GLOBAL_ERROR, true)
    });
  }

  getImportTemplateCostType() {
    const scopeId = this.getScopeId();
    return this.http.get(`${this.apiImportTemplateCostType}?scopeId=${scopeId}`, { responseType: 'blob', observe: 'response' });
  }

  //#region Product Brand
  private apiImportTemplateProductBrand = `${this.baseURL}/${this.baseImportTemplate}/ProductBrand`;
  private apiProductBrandImport = `${this.baseURL}/${this.baseImport}/ProductBrand`;
  private apiGetProductBrandColumns = `${this.baseURL}/${this.baseColumns}/ProductBrand`;

  GetProductBrandColumns() {
    return this.cache.get(`apiGetProductBrandColumns`, () => this.http.get(this.apiGetProductBrandColumns));
  }

  GetImportTemplateProductBrand(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateProductBrand}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportProductBrand(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiProductBrandImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Product Dept
  private apiImportTemplateProductDept = `${this.baseURL}/${this.baseImportTemplate}/ProductDepartment`;
  private apiProductDeptImport = `${this.baseURL}/${this.baseImport}/ProductDepartment`;
  private apiGetProductDeptColumns = `${this.baseURL}/${this.baseColumns}/ProductDepartment`;

  GetProductDeptColumns() {
    return this.cache.get(`apiGetProductDeptColumns`, () => this.http.get(this.apiGetProductDeptColumns));
  }

  GetImportTemplateProductDept(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateProductDept}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportProductDept(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiProductDeptImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Prod Category
  private apiImportTemplateProductCategory = `${this.baseURL}/${this.baseImportTemplate}/ProductCategory`;
  private apiProductCategoryImport = `${this.baseURL}/${this.baseImport}/ProductCategory`;
  private apiGetProductCategoryColumns = `${this.baseURL}/${this.baseColumns}/ProductCategory`;

  GetProductCategoryColumns() {
    return this.cache.get(`apiGetProductCategoryColumns`, () => this.http.get(this.apiGetProductCategoryColumns));
  }

  GetImportTemplateProductCategory(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateProductCategory}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportProductCategory(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiProductCategoryImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Prod Ranking
  private apiImportTemplateProductRanking = `${this.baseURL}/${this.baseImportTemplate}/ProductRanking`;
  private apiProductRankingImport = `${this.baseURL}/${this.baseImport}/ProductRanking`;
  private apiGetProductRankingColumns = `${this.baseURL}/${this.baseColumns}/ProductRanking`;

  GetProductRankingColumns() {
    return this.cache.get(`apiGetProductRankingColumns`, () => this.http.get(this.apiGetProductRankingColumns));
  }

  GetImportTemplateProductRanking(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateProductRanking}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportProductRanking(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiProductRankingImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //region Product ERA
  private apiImportTemplateProductEra = `${this.baseURL}/${this.baseImportTemplate}/ProductEra`;
  private apiProductEraImport = `${this.baseURL}/${this.baseImport}/ProductEra`;
  private apiGetProductEraColumns = `${this.baseURL}/${this.baseColumns}/ProductEra`;

  GetProductEraColumns() {
    return this.cache.get(`apiGetProductEraColumns`, () => this.http.get(this.apiGetProductEraColumns));
  }

  GetImportTemplateProductEra(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateProductEra}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportProductEra(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiProductEraImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //region Dangerous Goods
  private apiImportTemplateDangerousGoods = `${this.baseURL}/${this.baseImportTemplate}/DangerousGoods`;
  private apiDangerousGoodsImport = `${this.baseURL}/${this.baseImport}/DangerousGoods`;
  private apiGetDangerousGoodsColumns = `${this.baseURL}/${this.baseColumns}/DangerousGoods`;

  GetDangerousGoodsColumns() {
    return this.cache.get(`apiGetDangerousGoodsColumns`, () => this.http.get(this.apiGetDangerousGoodsColumns));
  }

  GetImportTemplateDangerousGoods(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateDangerousGoods}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportDangerousGoods(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiDangerousGoodsImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Prod UOM
  private apiImportTemplateProductUOM = `${this.baseURL}/${this.baseImportTemplate}/ProductUom`;
  private apiProductUOMImport = `${this.baseURL}/${this.baseImport}/ProductUom`;
  private apiGetProductUOMColumns = `${this.baseURL}/${this.baseColumns}/ProductUom`;

  GetProductUOMColumns() {
    return this.cache.get(`apiGetProductUOMColumns`, () => this.http.get(this.apiGetProductUOMColumns));
  }

  GetImportTemplateProductUOM(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateProductUOM}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportProductUOM(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiProductUOMImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Conversion Rate
  private apiImportTemplateConversionRate = `${this.baseURL}/${this.baseImportTemplate}/ConversionRate`;
  private apiConversionRateImport = `${this.baseURL}/${this.baseImport}/ConversionRate`;
  private apiGetConversionRateColumns = `${this.baseURL}/${this.baseColumns}/ConversionRate`;

  GetConversionRateColumns() {
    return this.cache.get(`apiGetConversionRateColumns`, () => this.http.get(this.apiGetConversionRateColumns));
  }

  GetImportTemplateConversionRate(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateConversionRate}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportConversionRate(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiConversionRateImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Customer Profile
  private apiImportTemplateCustomerProfile = `${this.baseURL}/${this.baseImportTemplate}/Customer`;
  private apiCustomerProfileImport = `${this.baseURL}/${this.baseImport}/Customer`;
  private apiGetCustomerProfileColumns = `${this.baseURL}/${this.baseColumns}/Customer`;

  GetCustomerProfileColumns() {
    return this.cache.get(`apiGetCustomerProfileColumns`, () => this.http.get(this.apiGetCustomerProfileColumns));
  }

  GetImportTemplateCustomerProfiles(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateCustomerProfile}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportCustomerProfile(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiCustomerProfileImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Msic
  private apiImportTemplateMsic = `${this.baseURL}/${this.baseImportTemplate}/Msic`;
  private apiMsicImport = `${this.baseURL}/${this.baseImport}/Msic`;
  private apiGetMsicColumns = `${this.baseURL}/${this.baseColumns}/Msic`;

  GetMsicColumns() {
    return this.cache.get(`apiGetMsicColumns`, () => this.http.get(this.apiGetMsicColumns));
  }

  GetImportTemplateMsics(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateMsic}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportMsic(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiMsicImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Customer Class
  private apiImportTemplateCustomerClass = `${this.baseURL}/${this.baseImportTemplate}/CustomerClass`;
  private apiCustomerClassImport = `${this.baseURL}/${this.baseImport}/CustomerClass`;
  private apiGetCustomerClassColumns = `${this.baseURL}/${this.baseColumns}/CustomerClass`;

  GetCustomerClassColumns() {
    return this.cache.get(`apiGetCustomerClassColumns`, () => this.http.get(this.apiGetCustomerClassColumns));
  }

  GetImportTemplateCustomerClasses(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateCustomerClass}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportCustomerClass(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiCustomerClassImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Customer Group
  private apiImportTemplateCustomerGroup = `${this.baseURL}/${this.baseImportTemplate}/CustomerGroup`;
  private apiCustomerGroupImport = `${this.baseURL}/${this.baseImport}/CustomerGroup`;
  private apiGetCustomerGroupColumns = `${this.baseURL}/${this.baseColumns}/CustomerGroup`;

  GetCustomerGroupColumns() {
    return this.cache.get(`apiGetCustomerGroupColumns`, () => this.http.get(this.apiGetCustomerGroupColumns));
  }

  GetImportTemplateCustomerGroups(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateCustomerGroup}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportCustomerGroup(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiCustomerGroupImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Asset Type
  private apiImportTemplateAssetType = `${this.baseURL}/${this.baseImportTemplate}/AssetType`;
  private apiAssetTypeImport = `${this.baseURL}/${this.baseImport}/AssetType`;
  private apiGetAssetTypeColumns = `${this.baseURL}/${this.baseColumns}/AssetType`;

  GetAssetTypeColumns() {
    return this.cache.get(`apiGetAssetTypeColumns`, () => this.http.get(this.apiGetAssetTypeColumns));
  }

  GetImportTemplateAssetTypes(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateAssetType}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportAssetType(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiAssetTypeImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Voucher Profile
  private apiImportTemplateVoucher = `${this.baseURL}/${this.baseImportTemplate}/Voucher`;
  private apiVoucherImport = `${this.baseURL}/${this.baseImport}/Voucher`;
  private apiGetVoucherColumns = `${this.baseURL}/${this.baseColumns}/Voucher`;

  GetVoucherColumns() {
    return this.cache.get(`apiGetVoucherColumns`, () => this.http.get(this.apiGetVoucherColumns));
  }

  GetImportTemplateVoucher(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateVoucher}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportVoucher(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiVoucherImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region POS Asset
  private apiImportTemplatePosAsset = `${this.baseURL}/${this.baseImportTemplate}/POSAsset`;
  private apiPosAssetImport = `${this.baseURL}/${this.baseImport}/POSAsset`;
  private apiGetPosAssetColumns = `${this.baseURL}/${this.baseColumns}/POSAsset`;

  GetPosAssetColumns() {
    return this.cache.get(`apiGetPosAssetColumns`, () => this.http.get(this.apiGetPosAssetColumns));
  }

  GetImportTemplatePosAssets(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplatePosAsset}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportPosAsset(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiPosAssetImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Cash Shift
  private apiImportTemplateCashShift = `${this.baseURL}/${this.baseImportTemplate}/CashShift`;
  private apiCashShiftImport = `${this.baseURL}/${this.baseImport}/CashShift`;
  private apiGetCashShiftColumns = `${this.baseURL}/${this.baseColumns}/CashShift`;

  GetCashShiftColumns() {
    return this.cache.get(`apiGetCashShiftColumns`, () => this.http.get(this.apiGetCashShiftColumns));
  }

  GetImportTemplateCashShifts(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateCashShift}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportCashShift(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiCashShiftImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Content Management
  private apiImportTemplateContentManagement = `${this.baseURL}/${this.baseImportTemplate}/ContentManagement`;
  private apiContentManagementImport = `${this.baseURL}/${this.baseImport}/ContentManagement`;
  private apiGetContentManagementColumns = `${this.baseURL}/${this.baseColumns}/ContentManagement`;

  GetContentManagementColumns() {
    return this.cache.get(`apiGetContentManagementColumns`, () => this.http.get(this.apiGetContentManagementColumns));
  }

  GetImportTemplateContentManagements(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateContentManagement}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportContentManagement(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiContentManagementImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Kyb Doc Type
  private apiImportTemplateKybDocument = `${this.baseURL}/${this.baseImportTemplate}/KybDocument`;
  private apiKybDocumentImport = `${this.baseURL}/${this.baseImport}/KybDocument`;
  private apiGetKybDocumentColumns = `${this.baseURL}/${this.baseColumns}/KybDocument`;

  GetKybDocumentColumns() {
    return this.cache.get(`apiGetKybDocumentColumns`, () => this.http.get(this.apiGetKybDocumentColumns));
  }

  GetImportTemplateKybDocuments(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateKybDocument}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportKybDocument(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiKybDocumentImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Bank Listing
  private apiImportTemplateBankListing = `${this.baseURL}/${this.baseImportTemplate}/BankListing`;
  private apiBankListingImport = `${this.baseURL}/${this.baseImport}/BankListing`;
  private apiGetBankListingColumns = `${this.baseURL}/${this.baseColumns}/BankListing`;

  GetBankListingColumns() {
    return this.cache.get(`apiGetBankListingColumns`, () => this.http.get(this.apiGetBankListingColumns));
  }

  GetImportTemplateBankListings(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateBankListing}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportBankListing(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiBankListingImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Payment Method
  private apiImportTemplatePaymentMethod = `${this.baseURL}/${this.baseImportTemplate}/PaymentMethod`;
  private apiPaymentMethodImport = `${this.baseURL}/${this.baseImport}/PaymentMethod`;
  private apiGetPaymentMethodColumns = `${this.baseURL}/${this.baseColumns}/PaymentMethod`;

  GetPaymentMethodColumns() {
    return this.cache.get(`apiGetPaymentMethodColumns`, () => this.http.get(this.apiGetPaymentMethodColumns));
  }

  GetImportTemplatePaymentMethods(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplatePaymentMethod}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportPaymentMethod(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiPaymentMethodImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Price Profile
  private apiImportTemplatePriceProfile = `${this.baseURL}/${this.baseImportTemplate}/PriceProfile`;
  private apiPriceProfileImport = `${this.baseURL}/${this.baseImport}/PriceProfile`;
  private apiGetPriceProfileColumns = `${this.baseURL}/${this.baseColumns}/PriceProfile`;

  GetPriceProfileColumns() {
    return this.cache.get(`apiGetPriceProfileColumns`, () => this.http.get(this.apiGetPriceProfileColumns));
  }

  GetImportTemplatePriceProfile(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplatePriceProfile}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportPriceProfile(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiPriceProfileImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }
  //endregion Price Profile

  //#region Manual Disc
  private apiImportTemplateDiscountProfile = `${this.baseURL}/${this.baseImportTemplate}/DiscountProfile`;
  private apiDiscountProfileImport = `${this.baseURL}/${this.baseImport}/DiscountProfile`;
  private apiGetDiscountProfileColumns = `${this.baseURL}/${this.baseColumns}/ManualDiscount`;

  GetDiscountProfileColumns() {
    return this.cache.get(`apiGetDiscountProfileColumns`, () => this.http.get(this.apiGetDiscountProfileColumns));
  }

  GetImportTemplateDiscountProfiles(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateDiscountProfile}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportDiscountProfile(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiDiscountProfileImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Promotional Discount
  private apiImportTemplatePromotionalDiscount = `${this.baseURL}/${this.baseImportTemplate}/PromotionalDiscount`;
  private apiPromotionalDiscountImport = `${this.baseURL}/${this.baseImport}/PromotionalDiscount`;
  private apiGetPromotionalDiscountColumns = `${this.baseURL}/${this.baseColumns}/PromotionalDiscount`;

  GetPromotionalDiscountColumns() {
    return this.cache.get(`apiGetPromotionalDiscountColumns`, () => this.http.get(this.apiGetPromotionalDiscountColumns));
  }

  GetImportTemplatePromotionalDiscount(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplatePromotionalDiscount}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportPromotionalDiscount(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiPromotionalDiscountImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }
  //endregion Promotional Discount

  //#region Risk Tier
  private apiImportTemplateRiskTier = `${this.baseURL}/${this.baseImportTemplate}/RiskTier`;
  private apiRiskTierImport = `${this.baseURL}/${this.baseImport}/RiskTier`;
  private apiGetRiskTierColumns = `${this.baseURL}/${this.baseColumns}/RiskTier`;

  GetRiskTierColumns() {
    return this.cache.get(`apiGetRiskTierColumns`, () => this.http.get(this.apiGetRiskTierColumns));
  }

  GetImportTemplateRiskTiers(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateRiskTier}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportRiskTier(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiRiskTierImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Blacklist Reason
  private apiImportTemplateBlacklistReason = `${this.baseURL}/${this.baseImportTemplate}/BlacklistReason`;
  private apiBlacklistReasonImport = `${this.baseURL}/${this.baseImport}/BlacklistReason`;
  private apiGetBlacklistReasonColumns = `${this.baseURL}/${this.baseColumns}/BlacklistReason`;

  GetBlacklistReasonColumns() {
    return this.cache.get(`apiGetBlacklistReasonColumns`, () => this.http.get(this.apiGetBlacklistReasonColumns));
  }

  GetImportTemplateBlacklistReasons(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateBlacklistReason}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportBlacklistReason(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiBlacklistReasonImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region HS Code
  private apiImportTemplateHsCode = `${this.baseURL}/${this.baseImportTemplate}/HSCode`;
  private apiHsCodeImport = `${this.baseURL}/${this.baseImport}/HSCode`;
  private apiGetHsCodeColumns = `${this.baseURL}/${this.baseColumns}/HSCode`;

  GetHsCodeColumns() {
    return this.cache.get(`apiGetHsCodeColumns`, () => this.http.get(this.apiGetBlacklistReasonColumns));
  }

  GetImportTemplateHsCode(isUpdate: boolean, data: any) {
    return this.http.post(`${this.apiImportTemplateHsCode}`, data, {
      params: { isUpdate: isUpdate },
      responseType: 'blob',
      observe: 'response'
    });
  }

  ImportHsCode(isUpdate: boolean, formData: FormData) {
    return this.http.post(`${this.apiHsCodeImport}`, formData, { params: { isUpdate: isUpdate } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Export
  export(api: string, data: any, filenameModule: string) {
    const timestamp = this.datePipe.transform(new Date(), 'yyyyMMdd_HHmmss');
    let fileName = filenameModule + '_' + timestamp;
    return this.http
      .post(`${api}?exportMethod=1&fileName=${fileName}&delimeter=1`, data, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(
        switchMap((response: HttpResponse<Blob>) => {
          const contentType = response.headers.get('Content-Type') || '';

          // If server returns error as JSON in a blob
          if (contentType.includes('application/json')) {
            // Convert Blob to text, then parse JSON
            return new Observable<Blob>((observer) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result as string);
                  observer.error(json);
                } catch {
                  observer.error('Invalid JSON error response');
                }
              };
              reader.onerror = () => observer.error('Failed to read blob error');
              reader.readAsText(response.body!);
            });
          }

          // get file name from header
          // const contentDisposition = response.headers.get('Content-Disposition');
          // if (contentDisposition) {
          //   const match = contentDisposition.match(/filename="(.+?)"/);
          //   if (match && match[1]) {
          //     fileName = match[1];
          //   }
          // }

          const cleanResponse = {
            blob: response.body,
            fileName: fileName
          };
          // Success case: return blob
          return of(cleanResponse);
        }),
        catchError((err) => throwError(() => err))
      );
  }
}
